import { useState, useEffect } from 'react';
import blogAPIService from '../services/blogAPIService';

// Custom hook for managing blog data
export const useBlogs = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const blogs = await blogAPIService.fetchBlogs();
      setBlogPosts(blogs);
    } catch (err) {
      console.error('Error fetching blogs:', err);
      setError('Internal Server Error');
      // Don't use fallback data - keep posts empty to show error
      setBlogPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBlogById = async (id) => {
    try {
      setLoading(true);
      setError(null);
      const blog = await blogAPIService.fetchBlogById(id);
      return blog;
    } catch (err) {
      console.error('Error fetching blog by ID:', err);
      setError('Internal Server Error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const refreshBlogs = () => {
    fetchBlogs();
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  return {
    blogPosts,
    loading,
    error,
    fetchBlogById,
    refreshBlogs,
  };
};

// Custom hook for a single blog post
export const useBlog = (id) => {
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlog = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        setError(null);
        const blogData = await blogAPIService.fetchBlogById(id);
        setBlog(blogData);
      } catch (err) {
        console.error('Error fetching blog:', err);
        setError('Failed to load blog post.');
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [id]);

  return {
    blog,
    loading,
    error,
  };
};

// Custom hook for a single blog post with related posts
export const useBlogWithRelated = (id) => {
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlogWithRelated = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        const blogData = await blogAPIService.fetchSingleBlogWithRelated(id);
        setBlog(blogData);
      } catch (err) {
        console.error('Error fetching blog with related posts:', err);
        setError('Failed to load blog post with related content.');
      } finally {
        setLoading(false);
      }
    };

    fetchBlogWithRelated();
  }, [id]);

  return {
    blog,
    loading,
    error,
  };
};
